/*
 * Client half of the dev-only --at time travel (see bhs-shared/src/clock.ts).
 * The server renders the applied offset into a <meta name="clock-offset-ms">
 * (base.njk, only when non-zero); this reads it and shifts the browser's clock
 * by that fixed millisecond offset so client-rendered relative times
 * (live-time.ts, open-close-assignments.ts) match the time-travelled server.
 *
 * Patches this bundle's @js-temporal polyfill `Temporal.Now` and the global
 * Date. Each esbuild entry bundles its own polyfill instance, so an entry that
 * wants shifted client time imports and calls installClientClockOffset() at its
 * top. No-op (and no meta tag) in production.
 */

import { Temporal } from '@js-temporal/polyfill';

let installed = false;

// The genuinely-real epoch ms, captured before the Date patch swaps Date in.
const realDateNow = Date.now.bind(Date);

const readOffsetMs = (): number => {
  const raw = document.querySelector('meta[name="clock-offset-ms"]')?.getAttribute('content');
  const ms = raw ? Number(raw) : 0;
  return Number.isFinite(ms) ? ms : 0;
};

/*
 * Shift Temporal.Now: build every reading from realDateNow()+offset via
 * Instant.fromEpochMilliseconds so it is applied exactly once regardless of the
 * polyfill's internal clock source (its Now.instant() reads the patched Date,
 * which would otherwise double-count). Mutates the shared Now object, so
 * modules that captured it via `const { Now } = Temporal` see the shift.
 */
const patchTemporalNow = (epochNow: () => number): void => {
  // biome-ignore lint/suspicious/noExplicitAny: runtime monkeypatch of the Temporal.Now builtin
  const Now = Temporal.Now as any;
  if (Now.__clockShifted) return;
  const tzId = () => (typeof Now.timeZoneId === 'function' ? Now.timeZoneId() : undefined);
  const instant = () => Temporal.Instant.fromEpochMilliseconds(epochNow());
  Now.instant = () => instant();
  Now.zonedDateTimeISO = (tz?: string) => instant().toZonedDateTimeISO(tz ?? tzId());
  Now.plainDateTimeISO = (tz?: string) => Now.zonedDateTimeISO(tz).toPlainDateTime();
  Now.plainDateISO = (tz?: string) => Now.zonedDateTimeISO(tz).toPlainDate();
  Now.plainTimeISO = (tz?: string) => Now.zonedDateTimeISO(tz).toPlainTime();
  Now.__clockShifted = true;
};

const patchDate = (offsetMs: number): void => {
  const RealDate = window.Date;
  if ((RealDate as unknown as { __clockShifted?: boolean }).__clockShifted) return;
  class ShiftedDate extends RealDate {
    // biome-ignore lint/suspicious/noExplicitAny: mirroring Date's variadic constructor
    constructor(...args: any[]) {
      if (args.length === 0) {
        super(RealDate.now() + offsetMs);
      } else {
        // biome-ignore lint/suspicious/noExplicitAny: forwarding Date's overloaded args
        super(...(args as [any]));
      }
    }
    static now(): number {
      return RealDate.now() + offsetMs;
    }
  }
  (ShiftedDate as unknown as { __clockShifted?: boolean }).__clockShifted = true;
  window.Date = ShiftedDate as unknown as DateConstructor;
};

/*
 * If the server handed us a non-zero clock offset, shift Temporal.Now and Date.
 * Call once at the top of any client entry that renders time. Idempotent.
 */
export const installClientClockOffset = (): void => {
  if (installed) return;
  installed = true;
  const offsetMs = readOffsetMs();
  if (offsetMs === 0) return;
  patchTemporalNow(() => realDateNow() + offsetMs);
  patchDate(offsetMs);
};
